/**
 * The Hair card — ADR-039 T1, the Body tab's "Hair" section, in the soft-tissue
 * card's idiom: one decision per state, readings SAID on the status line, never
 * printed (Allen, 2026-09-30: panels carry decisions, not details).
 *
 *  - OFFERED: "Hair found — free / attached / mixed" with the skull drawn on the
 *    head and the region tinted. The skull's handle drags and scales (one-drag
 *    confirm); Confirm records it, Not hair sets it aside. Nothing is recorded
 *    until Confirm (an unconfirmed offer never changes anything downstream).
 *  - CONFIRMED: the treatment, ONE sentence on what the run did with it
 *    (ADR-039 T2a: attached hair softened; free / mixed waits for T3's chains;
 *    a refusal — never silent), the Gravity setting (0 = the hair holds its
 *    sculpt — the default), Adjust skull (re-confirm), Not hair (withdraw,
 *    undoable).
 *  - SET ASIDE: one quiet line and "Offer again".
 *  - NOT MEASURED / OTHER MESH: a pill whose why is revealed on the status line
 *    (a refusal must reach the eye).
 *
 * The skull and the tint show while the card's section is open (`visible`) — until the hair is
 * confirmed; then only while adjusting the skull or with the ➕ / ➖ wand armed (Allen, 2026-10-01:
 * the overlay must get out of the way of the animation).
 *
 * A pure view over `deps`: it renders what it is handed and reports input.
 * Depends on: panel-registry (types), tooltip, the controller's view type.
 * Depended on by: main.
 */
import type { PanelMount } from './panel-registry.js';
import { describeButton, withTip } from './tooltip.js';
import type { HairCardView } from '../hair/hair-controller.js';

export interface HairPanelDeps {
  readonly view: () => HairCardView;
  readonly mark?: (subject: string) => HTMLElement | null;
  readonly confirm: (trusted: boolean) => void;
  readonly notHair: () => void;
  readonly offerAgain: () => void;
  readonly resetSkull: () => void;
  readonly startAdjust: () => void;
  readonly setGravity: (gravity: number) => void;
  /** Arm (or put away) the zone wand on the confirmed hair region; trusted clicks only. */
  readonly wand?: (mode: 'add' | 'remove' | null, trusted: boolean) => void;
  readonly wandMode?: () => 'add' | 'remove' | null;
  /** The softened hair's Bounce / Sway / Jiggle as the Document holds them, or null when nothing was softened. */
  readonly motion?: () => { readonly bounce: number; readonly sway: number; readonly jiggle: number } | null;
  /** While a slider moves: retune the playing spring only (nothing recorded yet). */
  readonly previewMotion?: (controls: Partial<{ bounce: number; sway: number; jiggle: number }>) => void;
  /** On release: ONE undoable `hair.setMotion` — the Document keeps it through new clips, re-runs and reloads. */
  readonly setMotion?: (motion: { bounce: number; sway: number; jiggle: number }) => void;
  /** The card's section opened or closed (or the card left): draw the skull and tint, or not. */
  readonly visible: (on: boolean) => void;
  readonly say?: (line: string | null, reveal?: boolean) => void;
  readonly subjectChanged?: (listener: () => void) => () => void;
  /** Redraw when the controller's state moves outside the store (a drag's re-measure). */
  readonly changed?: (listener: () => void) => () => void;
}

const WORD = { attached: 'attached', free: 'free', mixed: 'mixed' } as const;
const TIP = {
  attached: 'lies on the head and body — it softens: a small, stiff bounce and a surface ripple, held at the scalp and where it rests',
  free: 'stands free of the head on most sides — it will get spring chains (T3, not built yet)',
  mixed: 'part lies on the head, part stands free — chains for the free part come with T3 (not built yet)',
} as const;

function pill(word: string, state: 'fine' | 'check'): HTMLElement {
  const el = document.createElement('span');
  el.className = 'status-pill';
  el.dataset['state'] = state;
  el.textContent = word;
  return el;
}
function button(text: string, primary: boolean, tip: string, onClick: (event: MouseEvent) => void): HTMLElement[] {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = primary ? 'hand-apply' : 'hand-revert';
  b.textContent = text;
  b.addEventListener('click', onClick);
  return withTip(b, tip);
}

export function createHairPanel(deps: HairPanelDeps): PanelMount {
  return (container, store) => {
    const root = document.createElement('div');
    root.className = 'hair-panel';
    root.dataset['testid'] = 'hair';
    container.append(root);
    let acted = false;
    // Allen's field read, 2026-10-04: a first launch opened on the boot body with its UNCONFIRMED offer
    // painted on the head (skull + tint), as if chosen. An offer is drawn only when asked: hovering the
    // card previews it, "Show on head" pins it (to drag the skull). A draft or an adjustment draws as before.
    let pinned = false;
    let hovered = false;
    // A hover that was already there does not ask (Codex P2, #249 r1–r3). Two mutes:
    //  - after Hide (clicked FROM the card), only LEAVING the card clears it — a nudge must not undo Hide;
    //  - after a new body lands (possibly drawing the card under a resting pointer), the pointer has to
    //    actually MOVE over the card, or leave it — the enter a re-render fires carries no movement.
    let hideMuted = false;
    let landMuted = true;
    const section = container.closest('details');
    const asked = (): boolean => {
      const v = deps.view();
      return v.state !== 'offered' || v.draft || pinned || (hovered && !hideMuted && !landMuted);
    };
    const showing = (): boolean => root.isConnected && (section === null || section.open) && asked();
    const onToggle = (): void => deps.visible(showing());
    const hover = (on: boolean) => (): void => {
      hovered = on;
      if (!on) {
        hideMuted = false;
        landMuted = false;
      }
      deps.visible(showing());
    };
    root.addEventListener('pointerenter', hover(true));
    root.addEventListener('pointerleave', hover(false));
    root.addEventListener('pointermove', (event: PointerEvent) => {
      hovered = true;
      if (landMuted && (event.movementX !== 0 || event.movementY !== 0)) {
        landMuted = false;
        deps.visible(showing());
      }
    });

    const render = (): void => {
      const v = deps.view();
      root.replaceChildren();
      if (v.state === null) return;
      const card = document.createElement('section');
      card.className = 'hand-card hair-card';
      card.dataset['state'] = v.state;
      if (v.treatment !== null) card.dataset['treatment'] = v.treatment;
      const title = document.createElement('h3');
      title.textContent = 'Hair';
      const mark = deps.mark?.('Hair') ?? null;
      if (mark !== null) title.append(mark);
      card.append(title);
      // the reading is said on hover and focus; a refusal is revealed
      const refusing = v.state === 'refused' || v.state === 'stale';
      card.addEventListener('pointerenter', () => deps.say?.(v.line, refusing));
      card.addEventListener('focusin', () => deps.say?.(v.line, refusing));
      if (acted) {
        acted = false;
        deps.say?.(v.line);
      }
      const act = (f: () => void) => () => {
        acted = true;
        f();
      };

      if (v.state === 'refused') {
        title.append(' ', pill('not measured', 'check'));
      } else if (v.state === 'stale') {
        title.append(' ', pill('other mesh', 'check'));
        card.append(...button('Not hair', false, 'withdraw the hair declared on another mesh (undoable)', act(deps.notHair)));
      } else if (v.state === 'dismissed') {
        card.classList.add('hair-quiet');
        const line = document.createElement('p');
        line.className = 'hair-line';
        line.textContent = 'Set aside — nothing changes.';
        card.append(line, ...button('Offer again', false, 'show the hair the detector found again', act(deps.offerAgain)));
      } else {
        // offered / confirmed: the verdict is the decision this card carries
        const head = document.createElement('p');
        head.className = 'hair-line';
        const word = v.treatment === null ? 'nothing over this skull' : WORD[v.treatment];
        head.textContent = v.state === 'offered' ? `Hair found — ${word}` : `Hair — ${word}${v.draft ? ' (skull moved)' : ''}`;
        card.append(...(v.treatment === null ? [head] : withTip(head, TIP[v.treatment])));
        if (v.state === 'offered') card.classList.add('soft-tissue-offer');
        if (v.reason !== null && v.state === 'offered') title.append(' ', pill('check the skull', 'check'));

        if (v.state === 'confirmed' && !v.adjusting && v.note !== null) {
          const note = document.createElement('p');
          note.className = 'hair-line hair-note';
          note.textContent = v.note;
          card.append(note);
        }

        // Gravity only means something for hair that hangs (T3's chains); attached hair holds its sculpt.
        if (v.state === 'confirmed' && !v.adjusting && v.treatment !== 'attached') {
          const row = document.createElement('div');
          row.className = 'value-row tail-spring-knob';
          const label = document.createElement('span');
          label.className = 'vr-label';
          label.textContent = 'Gravity';
          const slider = document.createElement('input');
          slider.type = 'range';
          slider.min = '0';
          slider.max = '1';
          slider.step = '0.05';
          slider.value = String(v.gravity);
          slider.setAttribute('aria-label', 'Hair gravity');
          const value = document.createElement('span');
          value.className = 'vr-field';
          value.textContent = v.gravity.toFixed(2);
          slider.addEventListener('input', () => {
            value.textContent = Number(slider.value).toFixed(2);
          });
          // one set on release (the arm-space discipline): no physics to preview in T1
          slider.addEventListener('change', () => deps.setGravity(Number(slider.value)));
          row.append(label, ...withTip(slider, '0 holds the sculpt: the hair rests as it was made, floating hair stays up. Turn it up to let it hang.'), value);
          card.append(row);
        }

        // Allen's second field read (2026-10-01): "I no longer have any slider to control bounce or jiggle".
        // The leaf's own three, live while the clip plays: up-and-down, side-to-side, the surface ripple.
        const motion = v.state === 'confirmed' && !v.adjusting ? (deps.motion?.() ?? null) : null;
        if (motion !== null && deps.setMotion !== undefined) {
          for (const [key, label, tip] of [
            ['bounce', 'Bounce', 'how far the hair lifts and drops with each step — 0 still, 3 triples it'],
            ['sway', 'Sway', 'how far the hair swings side to side — 0 still, 3 triples it'],
            ['jiggle', 'Jiggle', 'the ripple across the hair as a bounce dies — 0 none, 3 the most'],
          ] as const) {
            const row = document.createElement('div');
            row.className = 'value-row tail-spring-knob';
            const name = document.createElement('span');
            name.className = 'vr-label';
            name.textContent = label;
            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = '0';
            slider.max = '3';
            slider.step = '0.1';
            slider.value = String(motion[key]);
            slider.setAttribute('aria-label', `Hair ${label.toLowerCase()}`);
            slider.dataset['testid'] = `hair-${key}`;
            const value = document.createElement('span');
            value.className = 'vr-field';
            value.textContent = motion[key].toFixed(1);
            slider.addEventListener('input', () => {
              value.textContent = Number(slider.value).toFixed(1);
              deps.previewMotion?.({ [key]: Number(slider.value) });
            });
            slider.addEventListener('change', () => deps.setMotion!({ ...motion, [key]: Number(slider.value) }));
            row.append(name, ...withTip(slider, tip), value);
            card.append(row);
          }
        }

        const actions = document.createElement('div');
        actions.className = 'soft-tissue-actions';
        if (v.state === 'offered' || v.adjusting) {
          actions.append(
            ...button(v.state === 'offered' ? 'Confirm' : 'Confirm skull', true, 'record this region as hair, with this skull (undoable). Harry will weight it itself.', (event) => {
              acted = true;
              deps.confirm(event.isTrusted);
            }),
          );
          if (v.draft) actions.append(...button('Reset skull', false, 'put the skull back where it was measured', act(deps.resetSkull)));
          if (v.state === 'offered' && !v.draft) {
            const [show, ...rest] = button(
              pinned ? 'Hide' : 'Show on head',
              false,
              pinned ? 'take the skull and the tinted hair off the head' : 'draw the measured skull and the hair it finds on the head — drag the skull to move it',
              () => {
                pinned = !pinned;
                hideMuted = !pinned && hovered; // only a pointer already over the card is muted (Codex P2, #249 r5)
                render();
                // The toggle was replaced by the render: keep a keyboard user on it (Codex P2, #249 r4).
                root.querySelector<HTMLElement>('[data-testid="hair-show"]')?.focus();
              },
            );
            show!.dataset['testid'] = 'hair-show';
            show!.setAttribute('aria-pressed', String(pinned));
            actions.append(show!, ...rest);
          }
        } else {
          actions.append(...button('Adjust skull', false, 'drag or scale the skull again; Confirm records the new region', act(deps.startAdjust)));
          if (deps.wand !== undefined) {
            const armed = deps.wandMode?.() ?? null;
            for (const [mode, text, tip] of [
              ['add', '➕', 'add hair: click flesh the region missed (a lock on the chest, a fringe) — as many clicks as you like, then Done'],
              ['remove', '➖', 'remove: click flesh that is not hair (an ear, the neck) — then Done'],
            ] as const) {
              const [b, ...rest] = button(text, false, tip, (event) => deps.wand!(mode, event.isTrusted));
              b!.setAttribute('aria-pressed', String(armed === mode));
              b!.dataset['testid'] = `hair-wand-${mode}`;
              actions.append(b!, ...rest);
            }
            if (armed !== null) {
              const [done, ...rest] = button('Done', true, 'put the wand away and soften the hair as drawn', (event) => deps.wand!(null, event.isTrusted));
              done!.dataset['testid'] = 'hair-wand-done';
              actions.prepend(done!, ...rest);
            }
          }
        }
        actions.append(
          ...button(
            'Not hair',
            false,
            v.state === 'offered' ? 'set this offer aside — nothing is recorded' : 'withdraw the hair: the region and its skull go (undoable)',
            act(deps.notHair),
          ),
        );
        card.append(actions);
        title.append(
          ' ',
          describeButton('hair', () =>
            'The outline on the head is the skull Harry measured. If it is off, drag its round handle to move it; ' +
            'the wheel over the handle scales it (Shift: height only); arrows nudge it. The tinted flesh is what Confirm records as hair.',
          ),
        );
      }
      root.append(card);
      deps.visible(showing());
    };
    section?.addEventListener('toggle', onToggle);
    const unsubscribe = store.subscribe(render);
    const unsubscribeSubject =
      deps.subjectChanged?.(() => {
        pinned = false; // a new body's offer starts unasked —
        landMuted = true; // even under a resting pointer (a drop onto the panel; Codex P2, #249 r2–r3)
        render();
      }) ?? (() => undefined);
    const unsubscribeChanged = deps.changed?.(render) ?? (() => undefined);
    render();
    return {
      dispose() {
        section?.removeEventListener('toggle', onToggle);
        unsubscribe();
        unsubscribeSubject();
        unsubscribeChanged();
        root.remove();
        deps.visible(false);
      },
    };
  };
}
